import { loadGpxFromFile, loadGpxFromUrl } from "./gpx.js";
import { createMap, fitBoundsAndWait } from "./map.js";
import { MAX_GPX_BYTES, formatDistance, formatSpeed } from "./utils.js";

export function bootInstance(root) {
  const mapEl = root.querySelector(".bhgpxs__map");
  const mapContainer = root.querySelector(".bhgpxs__mapContainer");
  const controls = root.querySelector(".bhgpxs__controls");
  const gpxInput = root.querySelector(".bhgpxs__gpxInput");
  const dropzone = controls.querySelector(".bhgpxs__dropzone");
  const average = root.querySelector(".bhgpxs__average");
  const averageSpeed = root.querySelector('[data-stat="average-speed"]');
  const kmStats = root.querySelector(".bhgpxs__kmStats");
  const height = root.dataset.height || "600px";

  if (!window.L || !mapEl) {
    return;
  }

  mapEl.style.height = height;
  if (mapContainer) {
    mapContainer.style.height = height;
  }

  const map = createMap(mapEl);
  let currentGpxLayer = null;
  let currentHighlightLayer = null;
  let lastGpxBounds = null;

  gpxInput.addEventListener("change", (event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      loadGPXFromFile(file);
    }
    event.target.value = "";
  });

  ["dragenter", "dragover"].forEach((eventName) => {
    dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzone.classList.add("dragover");
    });
  });

  ["dragleave", "drop"].forEach((eventName) => {
    dropzone.addEventListener(eventName, (event) => {
      event.preventDefault();
      event.stopPropagation();
      dropzone.classList.remove("dragover");
    });
  });

  dropzone.addEventListener("drop", (event) => {
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0];
    if (file) {
      loadGPXFromFile(file);
    }
  });

  const initialGpxUrl = getInitialGpxUrl(root);
  if (initialGpxUrl) {
    loadGPXFromUrl(initialGpxUrl);
  }

  async function loadGPXFromFile(file) {
    if (!file) {
      return;
    }

    if (!file.name.toLowerCase().endsWith(".gpx")) {
      alert("Please upload a .gpx file.");
      return;
    }

    if (file.size > MAX_GPX_BYTES) {
      alert("GPX file is too large.");
      return;
    }

    await loadGPX(() => loadGpxFromFile(file), "Failed to load GPX: ");
  }

  async function loadGPXFromUrl(url) {
    await loadGPX(() => loadGpxFromUrl(url), "Failed to load GPX from URL: ");
  }

  async function loadGPX(load, errorPrefix) {
    clearCurrentGpx();
    clearStats();

    try {
      const result = await load();
      if (!result) {
        return;
      }

      currentGpxLayer = result.layer.addTo(map);
      if (result.bounds.isValid()) {
        await fitBoundsAndWait(map, result.bounds, { padding: [20, 20] });
        lastGpxBounds = result.bounds;
      }
      updateStats(result.profile);
    } catch (error) {
      alert(errorPrefix + (error && error.message ? error.message : error));
      console.error(error);
    }
  }

  function clearCurrentGpx() {
    clearHighlight();

    if (!currentGpxLayer) {
      return;
    }

    map.removeLayer(currentGpxLayer);
    currentGpxLayer = null;
    lastGpxBounds = null;
  }

  function clearStats() {
    if (average) {
      average.hidden = true;
    }
    if (averageSpeed) {
      averageSpeed.textContent = "-";
    }
    if (kmStats) {
      kmStats.hidden = true;
      kmStats.innerHTML = "";
    }
    clearHighlight();
  }

  function updateStats(profile) {
    if (!profile) {
      return;
    }

    if (average && averageSpeed) {
      averageSpeed.textContent = formatSpeed(profile.averageSpeed);
      average.hidden = false;
    }

    if (kmStats) {
      kmStats.innerHTML = profile.kmSplits.map((split) => `
        <button class="bhgpxs__kmStat" type="button" aria-label="${formatSplitRange(split)} average speed ${formatSpeed(split.averageSpeed)}">
          <span>${formatSplitRange(split)}</span>
          <strong>${formatSpeed(split.averageSpeed)}</strong>
        </button>
      `).join("");
      bindSplitHighlights(profile.kmSplits);
      kmStats.hidden = !profile.kmSplits.length;
    }
  }

  function bindSplitHighlights(splits) {
    kmStats.querySelectorAll(".bhgpxs__kmStat").forEach((stat, index) => {
      const split = splits[index];

      stat.addEventListener("mouseenter", () => highlightSplit(split));
      stat.addEventListener("focus", () => highlightSplit(split));
      stat.addEventListener("mouseleave", clearHighlight);
      stat.addEventListener("blur", clearHighlight);
    });
  }

  function highlightSplit(split) {
    clearHighlight();

    if (!split || !split.pathSegments.length) {
      return;
    }

    currentHighlightLayer = L.featureGroup();
    split.pathSegments.forEach((pathSegment) => {
      L.polyline(pathSegment, {
        color: "#111827",
        interactive: false,
        lineCap: "round",
        lineJoin: "round",
        opacity: 0.9,
        weight: 10,
      }).addTo(currentHighlightLayer);
      L.polyline(pathSegment, {
        color: "#ffffff",
        interactive: false,
        lineCap: "round",
        lineJoin: "round",
        opacity: 0.95,
        weight: 4,
      }).addTo(currentHighlightLayer);
    });
    currentHighlightLayer.addTo(map);
    currentHighlightLayer.bringToFront();
  }

  function clearHighlight() {
    if (!currentHighlightLayer) {
      return;
    }

    map.removeLayer(currentHighlightLayer);
    currentHighlightLayer = null;
  }

  function formatSplitRange(split) {
    return `${formatDistance(split.startDistance)}-${formatDistance(split.endDistance)} km`;
  }
}

function getInitialGpxUrl(root) {
  return root.dataset.gpxUrl || new URLSearchParams(window.location.search).get("gpx");
}
